import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { apiService } from "../../api/services/apiService";
import type { PlatformTenantInsightsSummary } from "../../types";
import { TenantStatus } from "../../types";

const tablePaperSx = {
  borderRadius: 2,
  border: "1px solid",
  borderColor: "divider",
  bgcolor: "background.paper",
} as const;

const summaryCardSx = {
  p: 3,
  height: "100%",
  ...tablePaperSx,
} as const;

const TenantInsightsPanel: React.FC = () => {
  const [insights, setInsights] = useState<PlatformTenantInsightsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    apiService
      .getPlatformTenantInsights()
      .then(setInsights)
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : "Failed to load tenant insights"),
      )
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!insights) {
    return null;
  }

  return (
    <>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Paper sx={summaryCardSx}>
            <Typography variant="overline" color="text.secondary">
              Total Tenants
            </Typography>
            <Typography variant="h4" fontWeight={700}>
              {insights.totalTenants}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper sx={summaryCardSx}>
            <Typography variant="overline" color="text.secondary">
              Active Tenants
            </Typography>
            <Typography variant="h4" fontWeight={700} color="success.main">
              {insights.totalActiveTenants}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper sx={summaryCardSx}>
            <Typography variant="overline" color="text.secondary">
              Disabled Tenants
            </Typography>
            <Typography variant="h4" fontWeight={700} color="text.secondary">
              {insights.totalDisabledTenants}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Paper sx={{ p: 3, mb: 3, ...tablePaperSx }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Users per tenant
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Active tenant members only.
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Tenant ID</TableCell>
                <TableCell>Display name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Total users</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {insights.tenants.map((tenant) => (
                <TableRow key={`users-${tenant.tenantId}`}>
                  <TableCell>{tenant.tenantId}</TableCell>
                  <TableCell>{tenant.displayName ?? "—"}</TableCell>
                  <TableCell>
                    <Chip
                      label={
                        tenant.status === TenantStatus.ACTIVE ? "Enabled" : "Disabled"
                      }
                      color={tenant.status === TenantStatus.ACTIVE ? "success" : "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">{tenant.totalUsers}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper sx={{ p: 3, mb: 3, ...tablePaperSx }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Users by role per tenant
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Role counts include members with multiple roles.
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Tenant ID</TableCell>
                <TableCell>Display name</TableCell>
                <TableCell>Role</TableCell>
                <TableCell align="right">Users</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {insights.tenants.flatMap((tenant) =>
                tenant.usersByRole.length === 0 ? (
                  <TableRow key={`roles-empty-${tenant.tenantId}`}>
                    <TableCell>{tenant.tenantId}</TableCell>
                    <TableCell>{tenant.displayName ?? "—"}</TableCell>
                    <TableCell colSpan={2}>
                      <Typography variant="body2" color="text.secondary">
                        No roles assigned
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  tenant.usersByRole.map((roleCount, index) => (
                    <TableRow key={`roles-${tenant.tenantId}-${roleCount.role}`}>
                      <TableCell>{index === 0 ? tenant.tenantId : ""}</TableCell>
                      <TableCell>{index === 0 ? (tenant.displayName ?? "—") : ""}</TableCell>
                      <TableCell>{roleCount.role}</TableCell>
                      <TableCell align="right">{roleCount.count}</TableCell>
                    </TableRow>
                  ))
                ),
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper sx={{ p: 3, ...tablePaperSx }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Content per tenant
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Entity counts across products, APIs, categories, FAQs, links, documents, and support
          cases.
        </Typography>
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Tenant ID</TableCell>
                <TableCell>Display name</TableCell>
                <TableCell align="right">Products</TableCell>
                <TableCell align="right">APIs</TableCell>
                <TableCell align="right">Categories</TableCell>
                <TableCell align="right">FAQs</TableCell>
                <TableCell align="right">Links</TableCell>
                <TableCell align="right">Docs</TableCell>
                <TableCell align="right">Support cases</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {insights.tenants.map((tenant) => (
                <TableRow key={`content-${tenant.tenantId}`}>
                  <TableCell>{tenant.tenantId}</TableCell>
                  <TableCell>{tenant.displayName ?? "—"}</TableCell>
                  <TableCell align="right">{tenant.productCount}</TableCell>
                  <TableCell align="right">{tenant.apiCount}</TableCell>
                  <TableCell align="right">{tenant.categoryCount}</TableCell>
                  <TableCell align="right">{tenant.faqCount}</TableCell>
                  <TableCell align="right">{tenant.linkCount}</TableCell>
                  <TableCell align="right">{tenant.documentCount}</TableCell>
                  <TableCell align="right">{tenant.supportCaseCount}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </>
  );
};

export default TenantInsightsPanel;
